/**
 * 組織レポート デモページ（20名ダミー → 組織レポート確認用）
 *
 * N人分のダミー個人スコアを生成し、orgScore.ts の集約ロジックで算出した
 * OrgReportData を Gb74OrgReport に渡して表示する。人数の変更・再生成が可能。
 *
 * 注: デモ専用。スコアは実集約ロジック由来だが、回答データはランダム生成のダミー。
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Slider,
  Typography,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Gb74OrgReport } from './Gb74OrgReport';
import { generateDummyOrgReport } from '../utils/generateDummyOrgReport';
import { generateConsultNetwork } from '../utils/dummyConsultNetwork';

type LoadScenario = 'balanced' | 'polarized' | 'homogeneous';

export const OrgReportDemoPage: React.FC = () => {
  const [respondents, setRespondents] = useState(20);
  const [scenario, setScenario] = useState<LoadScenario>('balanced');
  // 再生成トリガー（ボタンで更新）
  const [seed, setSeed] = useState(0);

  const data = useMemo(
    () => generateDummyOrgReport({ respondents, scenario }),
    // seed を依存に含めることで「再生成」ボタンで作り直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [respondents, scenario, seed]
  );

  const network = useMemo(
    () => generateConsultNetwork({ respondents }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [respondents, seed]
  );

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', px: 2, py: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          組織レポート デモ（ダミー回答）
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          指定した人数のダミー個人スコアを生成し、確定済みの集約ロジック（働きがい／つながり／離職の兆し）で
          組織レポートを描画します。スコアは実ロジック由来、回答データはランダムなデモ用です。
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
          <Box sx={{ minWidth: 260 }}>
            <Typography variant="body2" gutterBottom>
              回答者数: <strong>{respondents}名</strong>
            </Typography>
            <Slider
              value={respondents}
              min={7}
              max={60}
              step={1}
              marks={[
                { value: 7, label: '7' },
                { value: 20, label: '20' },
                { value: 40, label: '40' },
                { value: 60, label: '60' },
              ]}
              onChange={(_, v) => setRespondents(v as number)}
              valueLabelDisplay="auto"
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => setSeed((s) => s + 1)}
          >
            ダミーを再生成
          </Button>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            §01 コミュニケーション負荷シナリオ（ハーマンの組み合わせ）
          </Typography>
          <ToggleButtonGroup
            value={scenario}
            exclusive
            size="small"
            onChange={(_, v) => v && setScenario(v as LoadScenario)}
          >
            <ToggleButton value="balanced">多様＋橋渡し（維持）</ToggleButton>
            <ToggleButton value="polarized">対角分極＋橋渡し薄（減点）</ToggleButton>
            <ToggleButton value="homogeneous">同質（脆さ減点）</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            シナリオで組織ダイヤの組み合わせを差し替え、つながりスコアへの負荷補正の効き方を比較できます（レベル3仮説・検証中）。
          </Typography>
        </Box>
      </Paper>

      {/* §03 は consultNetwork を渡して レベルB(部門ピル＋クリック展開) で表示 (基本方針2: 匿名) */}
      {/* 03改B を採用し §03本体に組込み済み。仕様確認用の 03改A(統合版)/03改B(単体) プロトタイプは撤去。 */}
      <Gb74OrgReport data={{ ...data, consultNetwork: network }} />
    </Box>
  );
};

export default OrgReportDemoPage;
